import { useState } from 'react';
import { IconAlertTriangle, IconUser, IconWallet } from '@tabler/icons-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  accountsLinkedToAppUser,
  type CustomerGraph,
  type GraphWallet,
} from '../customerGraph';
import { WalletPane } from './WalletPane';

interface WalletSearchResultProps {
  graph: CustomerGraph;
  walletAccountNumber: string;
  canReleaseHold: boolean;
  /** Jump to the mobile-account view for a linked account. */
  onOpenAccount?: (msisdn: string) => void;
}

/**
 * Result of a wallet-number search: who owns the wallet, which mobile
 * accounts they are linked to, and the wallet pane scoped to that number.
 */
export function WalletSearchResult({
  graph,
  walletAccountNumber,
  canReleaseHold,
  onOpenAccount,
}: WalletSearchResultProps) {
  const [showAll, setShowAll] = useState(false);
  const wallet: GraphWallet | undefined =
    graph.wallets.find((candidate) => candidate.walletAccountNumber === walletAccountNumber) ??
    graph.wallets.find((candidate) => candidate.hop === 0);
  const owner = wallet
    ? graph.appUsers.find((user) => user.appUserUuid === wallet.ownerAppUserUuid)
    : undefined;
  const linkedAccounts = owner ? accountsLinkedToAppUser(graph, owner.appUserUuid) : [];
  const otherWallets = owner
    ? graph.wallets.filter(
        (candidate) =>
          candidate.ownerAppUserUuid === owner.appUserUuid &&
          candidate.walletUuid !== wallet?.walletUuid,
      )
    : [];
  const noFicaIdentity = owner
    ? graph.warnings.some(
        (warning) => warning.kind === 'NO_FICA_IDENTITY' && warning.appUserUuid === owner.appUserUuid,
      )
    : false;
  const identity = owner?.ficaEntityUuid
    ? graph.identities.find((candidate) => candidate.entityUuid === owner.ficaEntityUuid)
    : undefined;

  if (!wallet || !owner) {
    return (
      <Alert variant="destructive" className="mt-6">
        <AlertDescription>
          Wallet {walletAccountNumber} was found in {graph.tenant.displayName ?? graph.tenant.mvno} but its
          owner could not be resolved.
        </AlertDescription>
      </Alert>
    );
  }

  const ownerName = [owner.firstName, owner.lastName].filter(Boolean).join(' ') || owner.username || owner.email || owner.appUserUuid;

  return (
    <div className="mt-6 min-w-0 max-w-full">
      <div className="mb-4 rounded-lg border border-border bg-card p-4 shadow-sm lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <IconWallet className="size-5" />
              Wallet <span className="font-mono">{wallet.walletAccountNumber}</span>
              <Badge variant="outline">{graph.tenant.displayName ?? graph.tenant.mvno}</Badge>
              {wallet.status && <Badge variant="secondary">{wallet.status}</Badge>}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{wallet.name || 'Wallet account'}</p>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Owner (app user)</dt>
            <dd className="mt-1 flex flex-wrap items-center gap-2">
              <IconUser className="size-4 text-muted-foreground" />
              <span className="font-medium">{ownerName}</span>
              {owner.status && <Badge variant="outline">{owner.status}</Badge>}
            </dd>
            <dd className="mt-1 text-xs text-muted-foreground">
              {owner.username && <span>@{owner.username} </span>}
              {owner.email && <span>{owner.email} </span>}
              {owner.recoveryPhoneNumber && <span>{owner.recoveryPhoneNumber}</span>}
            </dd>
            <dd className="mt-1 break-all font-mono text-xs text-muted-foreground">{owner.appUserUuid}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Wallet identity (FICA)</dt>
            <dd className="mt-1">
              {identity ? (
                <>
                  <span className="font-medium">
                    {[identity.firstName, identity.lastName].filter(Boolean).join(' ') || 'Identity on file'}
                  </span>
                  {identity.idNumber && <span className="ml-2 font-mono text-xs">{identity.idNumber}</span>}
                </>
              ) : (
                <span className="text-muted-foreground">No FICA identity linked</span>
              )}
            </dd>
            <dd className="mt-1 text-xs text-muted-foreground">
              Tier {owner.ficaTier ?? 'NONE'}
              {identity?.identityAssuranceLevel ? ` · ${identity.identityAssuranceLevel}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Linked mobile accounts</dt>
            <dd className="mt-1 flex flex-wrap gap-2">
              {linkedAccounts.length === 0 && <span className="text-muted-foreground">None</span>}
              {linkedAccounts.map(({ account, isPrimary, verified }) => (
                <Button
                  key={account.accountUuid}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 font-mono text-xs"
                  disabled={!account.msisdn || !onOpenAccount}
                  onClick={() => account.msisdn && onOpenAccount?.(account.msisdn)}
                >
                  {account.msisdn ?? account.accountUuid.slice(0, 8)}
                  {isPrimary && <Badge variant="secondary" className="h-4 px-1 text-[10px]">primary</Badge>}
                  {!verified && <Badge variant="outline" className="h-4 px-1 text-[10px]">unverified</Badge>}
                  {account.status && <span className="text-muted-foreground">{account.status}</span>}
                </Button>
              ))}
            </dd>
            {otherWallets.length > 0 && (
              <dd className="mt-2 text-xs text-muted-foreground">
                {otherWallets.length} other wallet(s) on this app user:{' '}
                {otherWallets.map((other) => other.walletAccountNumber).join(', ')}
              </dd>
            )}
          </div>
        </dl>

        {noFicaIdentity && (
          <Alert className="mt-4 border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
            <IconAlertTriangle className="size-4" />
            <AlertTitle>Wallet holder has no FICA identity</AlertTitle>
            <AlertDescription>
              This app user holds a wallet but has no linked identity in Nova. Wallet-funded purchases will be refused until one is recorded.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="min-w-0 rounded-lg border border-border bg-card shadow-sm">
        <WalletPane
          scope={{
            mvno: graph.tenant.mvno,
            appUserUuid: owner.appUserUuid,
            walletAccountNumber: showAll ? null : wallet.walletAccountNumber,
          }}
          canReleaseHold={canReleaseHold}
          onShowAllWallets={() => setShowAll(true)}
        />
      </div>
    </div>
  );
}
